import React from "react";
import {
  Alert,
  Box,
  Button,
  Collapse,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import type { SpecsValidationIssue } from "../../../service/SpecsRevisionService";
import { createStyles, palette } from "../../../styles/theme";
import { useSpecsPageContext } from "../SpecsPageContext";
import { SpecsFabricColorModel } from "../specsModel/fabrics/SpecsFabricColorModel";
import { SpecsFabricsModel } from "../specsModel/SpecsFabricsModel";
import {
  appendItemAtModelPath,
  getValueAtModelPath,
  setValueAtModelPath,
} from "../specsForm/modelPath";
import {
  getValidationIssueFrameSx,
  SpecsValidationIssueHint,
} from "../specsForm/SpecsValidationIssueHighlight";
import { getSpecsIssueMatchForModelPath } from "../specsValidationIssues";
import { FabricColorAdvancedDetails } from "./FabricColorAdvancedDetails";
import { CompactDisplayCheckboxCell } from "./CompactDisplayCheckboxCell";
import { CompactDisplayColorCell } from "./CompactDisplayColorCell";
import {
  CompactCheckboxCell,
  CompactColorCell,
  CompactDisplayTextCell,
  CompactTextCell,
} from "./FabricsSpecsTableEditorFields";

const styles = createStyles({
  tableContainer: {
    border: `1px solid ${palette.grey.lightGrey}`,
    borderRadius: 1,
    overflowX: "auto",
  },
  headCell: {
    whiteSpace: "nowrap",
    fontSize: 12,
    fontWeight: 700,
    px: 1,
    py: 1,
    bgcolor: palette.grey.lightBackground,
    borderBottom: `1px solid ${palette.grey.lightGrey}`,
  },
  bodyCell: {
    px: 1,
    py: 0.25,
    verticalAlign: "middle",
    whiteSpace: "nowrap",
  },
  collapsedCell: {
    p: 0,
    borderBottom: 0,
  },
  collapseBox: {
    p: 1,
  },
});

type ColorColumn = {
  key:
    | "name"
    | "hex"
    | "generatorHex"
    | "label"
    | "fallback"
    | "textWhite"
    | "techpackColorName"
    | "chartName"
    | "column"
    | "textureUrl"
    | "disabled"
    | "colorEndOfLife"
    | "migrateTo"
    | "colorSkuId";
  label: string;
  minWidth: number;
  maxWidth?: number;
};

const COLOR_COLUMNS: readonly ColorColumn[] = [
  { key: "name", label: "Name", minWidth: 110 },
  { key: "hex", label: "Pastille", minWidth: 118, maxWidth: 118 },
  {
    key: "generatorHex",
    label: "Couleur calibrée",
    minWidth: 118,
    maxWidth: 118,
  },
  { key: "label", label: "Label", minWidth: 84, maxWidth: 84 },
  { key: "fallback", label: "Fallback", minWidth: 96 },
  { key: "textWhite", label: "White", minWidth: 56 },
  { key: "techpackColorName", label: "Techpack", minWidth: 96 },
  { key: "chartName", label: "Chart", minWidth: 90 },
  { key: "column", label: "Column", minWidth: 84 },
  {
    key: "textureUrl",
    label: "Texture",
    minWidth: 92,
    maxWidth: 92,
  },
  { key: "disabled", label: "Disable", minWidth: 56 },
  { key: "colorEndOfLife", label: "EOL", minWidth: 52 },
  { key: "migrateTo", label: "Migrate", minWidth: 96 },
  { key: "colorSkuId", label: "SKU", minWidth: 84 },
];

function FabricColorRow({
  model,
  basePath,
  specName,
  removable,
  onRemove,
  rowLabel,
  editing,
  onEdit,
  onDone,
  validationIssues = [],
}: {
  model: SpecsFabricsModel;
  basePath: string;
  specName: string;
  removable: boolean;
  onRemove?: () => void;
  rowLabel: string;
  editing: boolean;
  onEdit: () => void;
  onDone: () => void;
  validationIssues?: SpecsValidationIssue[];
}) {
  const [expanded, setExpanded] = React.useState(false);
  const rowRef = React.useRef<HTMLTableRowElement | null>(null);
  const detailsRowRef = React.useRef<HTMLTableRowElement | null>(null);
  const rowIssueMatch = getSpecsIssueMatchForModelPath({
    issues: validationIssues,
    specType: "fabric",
    modelPath: basePath,
    includeAncestorTargets: true,
  });
  const getCellSx = (modelPath: string) => ({
    ...styles.bodyCell,
    ...getValidationIssueFrameSx(
      getSpecsIssueMatchForModelPath({
        issues: validationIssues,
        specType: "fabric",
        modelPath,
      })
    ),
  });

  const handleBlurCapture = React.useCallback(
    (event: React.FocusEvent<HTMLElement>) => {
      if (!editing) {
        return;
      }

      const nextTarget = event.relatedTarget;
      if (
        nextTarget instanceof Node &&
        (rowRef.current?.contains(nextTarget) ||
          detailsRowRef.current?.contains(nextTarget))
      ) {
        return;
      }

      onDone();
    },
    [editing, onDone]
  );

  return (
    <>
      <TableRow
        ref={rowRef}
        onBlurCapture={handleBlurCapture}
        sx={getValidationIssueFrameSx(rowIssueMatch)}
      >
        <TableCell sx={styles.bodyCell}>
          <IconButton
            size="small"
            onClick={() => setExpanded((current) => !current)}
          >
            {expanded ? (
              <ExpandLessIcon fontSize="small" />
            ) : (
              <ExpandMoreIcon fontSize="small" />
            )}
          </IconButton>
        </TableCell>
        {COLOR_COLUMNS.map((column) => {
          const path = `${basePath}.${column.key}`;
          const value = getValueAtModelPath(model, path);
          const cellWidth = column.minWidth;
          const cellMaxWidth = column.maxWidth;

          if (column.key === "hex" || column.key === "generatorHex") {
            return (
              <TableCell
                key={column.key}
                sx={{
                  ...getCellSx(path),
                  width: cellWidth,
                  ...(cellMaxWidth ? { maxWidth: cellMaxWidth } : null),
                }}
              >
                {editing ? (
                  <CompactColorCell
                    value={String(value ?? "")}
                    onChange={(nextValue) =>
                      setValueAtModelPath(model, path, nextValue)
                    }
                  />
                ) : (
                  <CompactDisplayColorCell
                    value={String(value ?? "")}
                    minWidth={column.minWidth}
                    maxWidth={column.maxWidth}
                    onColorChange={(nextValue) =>
                      setValueAtModelPath(model, path, nextValue)
                    }
                  />
                )}
              </TableCell>
            );
          }

          if (
            column.key === "textWhite" ||
            column.key === "disabled" ||
            column.key === "colorEndOfLife"
          ) {
            return (
              <TableCell
                key={column.key}
                sx={{
                  ...getCellSx(path),
                  width: cellWidth,
                  ...(cellMaxWidth ? { maxWidth: cellMaxWidth } : null),
                }}
              >
                {editing ? (
                  <CompactCheckboxCell
                    checked={Boolean(value)}
                    onChange={(nextValue) =>
                      setValueAtModelPath(model, path, nextValue)
                    }
                  />
                ) : (
                  <CompactDisplayCheckboxCell
                    checked={Boolean(value)}
                    minWidth={column.minWidth}
                    maxWidth={column.maxWidth}
                    onClick={onEdit}
                  />
                )}
              </TableCell>
            );
          }

          return (
            <TableCell
              key={column.key}
              sx={{
                ...getCellSx(path),
                width: cellWidth,
                ...(cellMaxWidth ? { maxWidth: cellMaxWidth } : null),
              }}
            >
              {editing ? (
                <CompactTextCell
                  value={String(value ?? "")}
                  minWidth={column.minWidth}
                  maxWidth={column.maxWidth}
                  uploadContext={
                    column.key === "textureUrl"
                      ? {
                          specType: "fabric",
                          specName: specName || undefined,
                          fieldPath: path,
                        }
                      : undefined
                  }
                  addLeftPreview={column.key === "textureUrl"}
                  onChange={(nextValue) =>
                    setValueAtModelPath(model, path, nextValue)
                  }
                />
              ) : (
                <CompactDisplayTextCell
                  value={String(value ?? "")}
                  minWidth={column.minWidth}
                  maxWidth={column.maxWidth}
                  addLeftPreview={column.key === "textureUrl"}
                  onClick={onEdit}
                />
              )}
            </TableCell>
          );
        })}
        <TableCell sx={styles.bodyCell}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography variant="caption" color="text.secondary">
              {rowLabel}
            </Typography>
            <Button
              size="small"
              color="inherit"
              onClick={editing ? onDone : onEdit}
            >
              {editing ? "Done" : "Edit"}
            </Button>
            {removable && onRemove ? (
              <IconButton size="small" onClick={onRemove}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            ) : null}
          </Stack>
          <SpecsValidationIssueHint match={rowIssueMatch} />
        </TableCell>
      </TableRow>
      <TableRow ref={detailsRowRef} onBlurCapture={handleBlurCapture}>
        <TableCell colSpan={COLOR_COLUMNS.length + 2} sx={styles.collapsedCell}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={styles.collapseBox}>
              <FabricColorAdvancedDetails
                model={model}
                basePath={basePath}
                validationIssues={validationIssues}
              />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

function FabricColorsTable({ rows }: { rows: React.ReactNode }) {
  return (
    <TableContainer sx={styles.tableContainer}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={styles.headCell} />
            {COLOR_COLUMNS.map((column) => (
              <TableCell
                key={column.key}
                sx={{
                  ...styles.headCell,
                  width: column.minWidth,
                  ...(column.maxWidth ? { maxWidth: column.maxWidth } : null),
                }}
              >
                {column.label}
              </TableCell>
            ))}
            <TableCell sx={styles.headCell}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>{rows}</TableBody>
      </Table>
    </TableContainer>
  );
}

export function FabricDefaultColorSection({
  model,
  validationIssues,
}: {
  model: SpecsFabricsModel;
  validationIssues?: SpecsValidationIssue[];
}) {
  const { selectedValue } = useSpecsPageContext();
  const [editingRow, setEditingRow] = React.useState<string | null>(null);

  return (
    <div>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="h2">Default Color</Typography>
        <Box sx={{ flex: 1 }} />
        {!model.fabric.defaultColor ? (
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={() =>
              setValueAtModelPath(
                model,
                "fabric.defaultColor",
                new SpecsFabricColorModel()
              )
            }
          >
            Add Default Color
          </Button>
        ) : null}
      </Stack>
      {model.fabric.defaultColor ? (
        <FabricColorsTable
          rows={
            <FabricColorRow
              model={model}
              basePath="fabric.defaultColor"
              specName={selectedValue}
              removable
              rowLabel="Default"
              editing={editingRow === "fabric.defaultColor"}
              onEdit={() => setEditingRow("fabric.defaultColor")}
              onDone={() => setEditingRow(null)}
              onRemove={() =>
                setValueAtModelPath(model, "fabric.defaultColor", null)
              }
              validationIssues={validationIssues}
            />
          }
        />
      ) : (
        <Alert severity="info">No default color configured.</Alert>
      )}
    </div>
  );
}

export function FabricColorsSection({
  model,
  validationIssues,
}: {
  model: SpecsFabricsModel;
  validationIssues?: SpecsValidationIssue[];
}) {
  const { selectedValue } = useSpecsPageContext();
  const [editingRow, setEditingRow] = React.useState<string | null>(null);

  return (
    <div>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="h2">Colors</Typography>
      </Stack>

      <FabricColorsTable
        rows={
          model.fabric.colors.length === 0 ? (
            <TableRow>
              <TableCell
                sx={styles.bodyCell}
                colSpan={COLOR_COLUMNS.length + 2}
              >
                <Alert severity="info">No colors yet.</Alert>
              </TableCell>
            </TableRow>
          ) : (
            <>
              {model.fabric.colors.map((_, index) => (
                <FabricColorRow
                  key={index}
                  model={model}
                  basePath={`fabric.colors.${index}`}
                  specName={selectedValue}
                  removable
                  rowLabel={`Color ${index + 1}`}
                  editing={editingRow === `fabric.colors.${index}`}
                  onEdit={() => setEditingRow(`fabric.colors.${index}`)}
                  onDone={() => setEditingRow(null)}
                  onRemove={() => model.fabric.colors.splice(index, 1)}
                  validationIssues={validationIssues}
                />
              ))}
            </>
          )
        }
      />

      <Box sx={{ mt: 1, display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={() =>
            appendItemAtModelPath(
              model,
              "fabric.colors",
              new SpecsFabricColorModel()
            )
          }
        >
          Add Color
        </Button>
      </Box>
    </div>
  );
}
